import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from "@angular/core";

import { LucideAngularModule, TriangleAlert, X } from "lucide-angular";

import { AuthService } from "@core/services/auth.service";
import { OrganizationHttpService } from "@features/admin/components/organizations/services/organization-http.service";
import { Organization } from "@shared/models/organization.model";

@Component({
  selector: "component-tokens-warning-banner",
  templateUrl: "./tokens-warning-banner.component.html",
  styleUrl: "./tokens-warning-banner.component.scss",
  imports: [LucideAngularModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TokensWarningBannerComponent {
  private readonly _authService = inject(AuthService);
  private readonly _organizationService = inject(OrganizationHttpService);

  protected readonly xIcon = X;
  protected readonly alertTriangleIcon = TriangleAlert;
  protected readonly warningPercentage = 10;

  protected readonly isVisible = signal(true);
  protected readonly organization = signal<Organization | null>(null);

  protected readonly shouldShow = computed(() => {
    if (!this.isVisible()) return false;

    const org = this.organization();
    if (!org?.plan?.tokensLimit || !org?.usage?.tokensUsed) return false;

    const limit = org.plan.tokensLimit;
    const used = org.usage.tokensUsed;
    const remaining = limit - used;
    const percentageLeft = (remaining / limit) * 100;

    return percentageLeft < this.warningPercentage;
  });

  constructor() {
    // Check dismissal state
    if (sessionStorage.getItem("tokens-warning-dismissed") === "true") {
      this.isVisible.set(false);
    }

    effect(() => {
      const user = this._authService.currentUser();
      if (user?.organizationId) {
        this._organizationService.getById(user.organizationId).subscribe({
          next: (org) => this.organization.set(org),
          error: () => this.organization.set(null)
        });
      } else {
        this.organization.set(null);
      }
    });
  }

  protected close(): void {
    this.isVisible.set(false);
    sessionStorage.setItem("tokens-warning-dismissed", "true");
  }
}
